import React from 'react';
import { TouchableOpacity } from 'react-native';

import { Box, TextAlignOptions } from '@styleseat/ui';
import PortalOverlay, { PortalOverlayDialog } from '../PortalOverlay';
import Button from '../Button';
import BodyText from '../BodyText';
import { HeaderText } from '../HeaderText';
import Stack from '../Stack';

import { CloseIcon } from '../Icons';
import PortalOverlayModal, { PortalOverlayModalProps } from '../PortalOverlay/PortalOverlayModal';

export type ConfirmationDialogProps = {
  /** Show the confirmation dialog */
  show: boolean;
  /** The title of the dialog. */
  title: string;
  /** Body text in the dialog */
  description?: string;
  /** Text for the confirmation button. */
  confirmText?: string;
  /** Text for the cancel button. */
  cancelText?: string;
  /** Fired when the user clicks the confirm button on the dialog. */
  onConfirm: () => void;
  /** Fired when the user clicks the cancel button on the dialog.
   * If no function is provided, cancel button will be hidden.
   */
  onCancel?: () => void;
  /** Fired when the close button is clicked.
   * If no function is provided, forces user to click confirm or cancel.
   */
  onClose?: () => void;
  /** Set which side the close button should align to */
  closeButtonSide?: 'left' | 'right';
  /** Flip styles of cancel and confirm buttons to encourage users to cancel the dialog */
  flipIntention?: boolean;
  /** Custom markup to be in the body of the dialog. */
  children?: React.ReactNode;
  testID?: string;
  position?: PortalOverlayModalProps['position'];
  textAlign?: TextAlignOptions;
};

/**
 * A configurable confirmation dialog component.
 *
 * @example
 * ```
 *  <ConfirmationDialog
 *    show
 *    title="Choose"
 *    description="Would you like free money?"
 *    onConfirm={handleConfirm}
 *    onCancel={handleCancel}
 *   />
 * ```
 */
const ConfirmationDialog: React.FCWithChildren<ConfirmationDialogProps> = ({
  show = false,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  onClose,
  closeButtonSide = 'right',
  flipIntention = false,
  children,
  testID,
  position,
  textAlign = 'center',
}) => {
  if (!show) {
    return null;
  }

  const ModalComponent = position ? PortalOverlayModal : PortalOverlayDialog;

  return (
    <PortalOverlay name="confirmation-dialog-container" onBackdropClick={onClose}>
      {/* @ts-expect-error */}
      <ModalComponent position={position}>
        <Box paddingTop={8} testID={testID}>
          {onClose && (
            <Box
              alignItems={closeButtonSide === 'right' ? 'flex-end' : 'flex-start'}
              paddingTop={4}
              paddingX={12}
            >
              <TouchableOpacity onPress={onClose} testID="confirmation-dialog-close">
                <CloseIcon width={20} height={20} />
              </TouchableOpacity>
            </Box>
          )}
          <Stack paddingX={16} paddingY={20} space={12}>
            <HeaderText level={2} textAlign={textAlign} testID="confirmation-dialog-title">
              {title}
            </HeaderText>
            {!!description && (
              <BodyText textAlign={textAlign} testID="confirmation-dialog-description">
                {description}
              </BodyText>
            )}
            {!!children && children}
            <Stack paddingTop={16} space={8}>
              <Button
                appearance={flipIntention ? 'outline-thin' : 'primary-neue'}
                onClick={onConfirm}
                testID="confirmation-dialog-confirm"
              >
                {confirmText}
              </Button>
              {onCancel && (
                <Button
                  appearance={flipIntention ? 'primary-neue' : 'outline-thin'}
                  onClick={onCancel}
                  testID="confirmation-dialog-cancel"
                >
                  {cancelText}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </ModalComponent>
    </PortalOverlay>
  );
};
export default ConfirmationDialog;
